import { toastError } from '@components/Toast/Toast';
import { CAPABILITY_ROLES } from '@constants/permissions';
import type { ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { Capability } from 'types/roles';

import type { Customer } from '../api/hooks/useProfiles';
import { legalType } from '../api/hooks/useProfiles';
import { useProfiles } from '../api/hooks/useProfiles';
import i18n from '../i18n';

export const KEY = 'gp-selected-profile';

const CUSTOMER_ID_PARAM = 'customerId';

const readUrlCustomerId = (): string | null => {
	const url = new URL(window.location.href);
	return url.searchParams.get(CUSTOMER_ID_PARAM)?.trim() || null;
};

const initialUrlCustomerId = readUrlCustomerId();

const ProfileContext = createContext<IProfileContext | null>(null);
interface IProfileContext {
	selectedProfile: Customer | null;
	hasCapability: (capability: Capability) => boolean;
	setSelectedProfile: (account: Customer | null) => void;
	isPersonal: boolean;
	profilesUnavailable: boolean;
}

export const removeSelectedProfile = () => {
	sessionStorage.removeItem(KEY);
};

const getStoredProfileId = (): string | null => {
	const storedAccount = sessionStorage.getItem(KEY);
	return storedAccount ? (JSON.parse(storedAccount)?.id ?? null) : null;
};

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
	const { data, isPending, isError } = useProfiles();

	// Before user data was set to localStorage, need to make sure it was removed from user's perspective for sure.
	useEffect(() => {
		if (localStorage.getItem(KEY)) {
			localStorage.removeItem(KEY);
		}
	}, []);

	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
		() => initialUrlCustomerId ?? getStoredProfileId(),
	);
	const pendingUrlCustomerIdRef = useRef<string | null>(initialUrlCustomerId);

	// Derive the active profile during render so it is available on the first
	// render after data loads (no null window that would flicker every gate).
	const selectedProfile = useMemo(() => {
		const customers = data?.customers;
		if (!customers?.length) {
			return null;
		}
		return (
			customers.find(
				(profile) => profile.id.toLowerCase() === selectedProfileId?.toLowerCase(),
			) ?? customers[0]
		);
	}, [data, selectedProfileId]);

	useEffect(() => {
		const requestedId = pendingUrlCustomerIdRef.current;
		if (!requestedId || !data?.customers?.length) {
			return;
		}
		pendingUrlCustomerIdRef.current = null;
		if (selectedProfile?.id.toLowerCase() !== requestedId.toLowerCase()) {
			toastError(i18n.t('common.customerSwitch.noCustomer'));
		}
	}, [data, selectedProfile]);

	const setSelectedProfile = useCallback((account: Customer | null) => {
		setSelectedProfileId(account?.id ?? null);
	}, []);

	const isPersonal = selectedProfile?.legalType === legalType.NATURAL_PERSON;

	useEffect(() => {
		if (selectedProfile) {
			sessionStorage.setItem(KEY, JSON.stringify(selectedProfile));
		}
	}, [selectedProfile]);

	const hasCapability = useCallback(
		(capability: Capability) =>
			!!selectedProfile && CAPABILITY_ROLES[capability].includes(selectedProfile.role),
		[selectedProfile],
	);

	// The profiles query settled without a usable profile (request failed or the
	// user has no customers), so selectedProfile will never be set.
	const profilesUnavailable = isError || (!isPending && !data?.customers?.length);

	const value = useMemo(
		() => ({
			selectedProfile,
			setSelectedProfile,
			hasCapability,
			isPersonal,
			profilesUnavailable,
		}),
		[selectedProfile, setSelectedProfile, hasCapability, isPersonal, profilesUnavailable],
	);

	return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useBankProfile = () => {
	const context = useContext(ProfileContext);
	if (!context) {
		throw new Error('useBankAccount must be used within a BankAccountProvider');
	}
	return context;
};
